import axiosConfig from "./axiosConfig";

// One rule in the operator-editable landlord-research prompt. Mirrors the BMA
// PromptRule (snake_case on the wire - the addendum round-trips as its model dump).
// `improvement` is operator-only documentation and is never sent to the model.
export interface PromptRuleModel {
  rule_id: string;
  category: string;
  title: string;
  guidance: string;
  why: string;
  limits: string;
  evidence: string;
  improvement: string;
  status: "active" | "draft" | "retired";
  position: number;
}

// The whole editable prompt: a free-text preamble plus an ordered rule set.
// `prompt_text` (on the revision) is the compiled render of this.
export interface PromptAddendumModel {
  preamble: string;
  rules: PromptRuleModel[];
  next_rule_seq: number;
}

// A saved revision the backend has DISCARDED from resolution because it was composed on an
// OLDER shipped seed than the one now deployed (`prompt_store.is_superseded_by_seed`): a
// deploy that ships a new built-in prompt resets every environment to it. The runs serve the
// seed instead, so the editor must say so rather than showing rules the operator did not
// save. Defined here (the base prompt-types module) rather than in `taskPrompt` so all three
// response shapes - landlord, people-ranking, task-generic - share ONE definition instead of
// each declaring the field and drifting.
export interface SupersededPromptRevision {
  revisionNumber: number;
  // The seed version it was built on. Null = saved before seed versions existed, which is
  // every revision saved before this shipped, so those supersede on the next deploy.
  seedVersion: number | null;
  createdBy: string;
  createdAt: string;
}

// The seed-reset fields every prompt-task response carries. Spread into each response
// interface so the shared editor can read them off any task's data.
export interface PromptSeedState {
  // Version of the shipped seed this environment is running.
  seedVersion: number;
  // Set only when a saved revision was superseded by a newer shipped seed.
  supersededRevision: SupersededPromptRevision | null;
}

// One revision of the operator-editable landlord-research prompt. The built-in
// seed is revision 1 (isSeed=true, no DB row); operator saves climb from 2.
export interface LandlordPromptRevision {
  revisionNumber: number;
  // The compiled render (what the agent runs on); `addendum` is its source.
  promptText: string;
  addendum: PromptAddendumModel;
  ruleCount: number;
  // Human summary of what this revision changed (auto-derived for per-rule edits).
  note: string | null;
  createdBy: string;
  createdAt: string | null;
  isActive: boolean;
  isSeed?: boolean;
}

export interface LandlordPromptResponse extends PromptSeedState {
  // The revision the research agent currently runs (a saved one, or the seed).
  active: LandlordPromptRevision;
  // The shipped built-in prompt text, for a "reset to default" action.
  defaultSeed: string;
  // The shipped built-in prompt as an addendum (whole text as the preamble, no
  // rules), for resetting the structured editor.
  defaultSeedAddendum: PromptAddendumModel;
  // The non-editable data-residency guardrail appended to whatever prompt is
  // active. Returned so the card can show it read-only.
  enforcedFooter: string;
  // Full history, newest first, ending with the synthetic seed (revision 1).
  revisions: LandlordPromptRevision[];
}

// The landlord-research prompt is task `landlord_research` on the unified
// task-scoped prompt endpoint (shared with the two people-ranking personas).
const getLandlordResearchPrompt = async (
  authToken: string
): Promise<LandlordPromptResponse> => {
  const { data } = await axiosConfig.get(`/leads/ai-prompt`, {
    params: { task: "landlord_research" },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getLandlordResearchPrompt;
